import { useEffect, useState, type ReactElement } from 'react';
import { usePushKey } from '../api/hooks';
import { disablePush, enablePush, pushState, type PushState } from '../lib/push';
import { useT } from '../lib/i18n';

/**
 * Notifications on **this device**, and every answer that question has.
 *
 * It sits on the reminders screen because that is the module that rings, and
 * because a setting nobody finds is a setting nobody turns on — the whole
 * difference between this module and a todo list with a clock in it is the
 * notification, and it cannot be two screens away.
 *
 * Five states are drawn, for the reason the list draws six: only one of them is
 * the one being designed for, and the other four are what somebody actually
 * meets. A browser that cannot ring, a browser that has been told not to, an
 * instance that handed out no key, off, and on.
 *
 * Beside them sits what the last press *produced*, which is a different question
 * — see `Working` below. Answering it with the state alone is what once left a
 * button appearing to do nothing at all.
 *
 * **`requestPermission()` is only ever reached from a real click.** A prompt on
 * mount is refused outright by some browsers, held against the origin by others,
 * and is in any case a question asked before anybody has seen what it is for.
 */

/**
 * What the last press produced, as distinct from what the toggle now is.
 *
 * `dismissed` earns its place: a browser may decline to put the permission
 * request on screen at all and answer "undecided" at once — Chrome does this
 * once somebody has waved such a prompt away, replacing it with an icon in the
 * address bar. Without this, the button appears simply not to work, which is
 * the report that produced it.
 */
type Working = 'idle' | 'working' | 'failed' | 'dismissed';

export function PushToggle(): ReactElement | null {
  const t = useT();
  const key = usePushKey();
  const [state, setState] = useState<PushState | null>(null);
  const [working, setWorking] = useState<Working>('idle');

  // Asked once, on mount. This reads the permission and the existing
  // subscription and asks for neither, so there is no prompt here.
  useEffect(() => {
    let current = true;
    void pushState().then((answer) => {
      if (current) setState(answer);
    });
    return () => {
      current = false;
    };
  }, []);

  // Nothing until the browser has answered. A strip that appears saying "off"
  // and corrects itself to "on" a moment later is worse than one that waits: the
  // first thing it says is the thing somebody acts on.
  if (state === null) return null;

  if (state === 'unsupported') {
    return <Notice title={t('push.unsupportedTitle')} body={t('push.unsupportedBody')} />;
  }

  if (state === 'denied') {
    return <Notice title={t('push.deniedTitle')} body={t('push.deniedBody')} />;
  }

  // The server handed out no key: nothing can be subscribed, and offering a
  // button that cannot work would be the one state that looks like a fault.
  if (!key.data?.publicKey) {
    if (key.isPending) return null;
    return <Notice title={t('push.unavailableTitle')} body={t('push.unavailableBody')} />;
  }

  const on = state === 'on';
  const publicKey = key.data.publicKey;

  const toggle = async (): Promise<void> => {
    setWorking('working');
    try {
      if (on) {
        setState(await disablePush());
        setWorking('idle');
        return;
      }

      const outcome = await enablePush(publicKey);
      // `dismissed` deliberately leaves the state alone: nothing was decided, so
      // the toggle is still off and still pressable — what changes is that the
      // strip now says where the answer has to be given.
      if (outcome === 'on') setState('on');
      if (outcome === 'denied') setState('denied');
      if (outcome === 'unsupported') setState('unsupported');
      setWorking(outcome === 'dismissed' || outcome === 'failed' ? outcome : 'idle');
    } catch {
      // A refused subscription, a push service that could not be reached, a
      // server that said no. None of them is worth a different sentence: the
      // action did not happen, and pressing again is the only useful next move.
      setWorking('failed');
    }
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-line bg-surface px-4 py-2.5 text-sm">
      <BellIcon on={on} />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-ink-900">{t('push.title')}</p>
        {/* Darker when the sentence is something to act on rather than a
            statement of the current state: the two are read differently, and a
            uniform grey is how the instruction gets skimmed past. */}
        <p
          className={working === 'idle' || working === 'working' ? 'text-ink-500' : 'text-ink-700'}
        >
          {working === 'dismissed'
            ? t('push.dismissed')
            : working === 'failed'
              ? t('push.failed')
              : t(on ? 'push.onBody' : 'push.offBody')}
        </p>
      </div>
      <button
        type="button"
        onClick={() => void toggle()}
        disabled={working === 'working'}
        // Announces what the press will do, not what the state is: the sentence
        // beside it already says the state, and a control that names it twice
        // leaves a screen reader to work out which one is the button.
        className={`min-h-9 shrink-0 rounded-lg px-3 font-semibold transition-colors disabled:opacity-60 ${
          on
            ? 'text-ink-500 hover:bg-paper hover:text-ink-900'
            : 'bg-accent text-accent-ink hover:bg-accent-dim'
        }`}
      >
        {working === 'working' ? t('push.working') : t(on ? 'push.disable' : 'push.enable')}
      </button>
    </div>
  );
}

/**
 * The states with nothing to press.
 *
 * Quiet on purpose: none of them is an error, and two of the three are simply
 * facts about the browser. A red panel would tell somebody to go looking for a
 * fault that is not there.
 */
function Notice({ title, body }: { title: string; body: string }): ReactElement {
  const t = useT();
  return (
    <div className="mt-3 rounded-lg border border-line bg-surface px-4 py-2.5 text-sm">
      <p className="font-medium text-ink-700">{title}</p>
      <p className="text-ink-500">{body}</p>
      <p className="text-ink-400">{t('push.perDevice')}</p>
    </div>
  );
}

function BellIcon({ on }: { on: boolean }): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      className={`size-5 shrink-0 ${on ? 'text-accent' : 'text-ink-400'}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 8a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7M13.7 20a2 2 0 0 1-3.4 0" />
      {/* The one difference between the two icons: a bell with a line through it
          says "off" without needing the word beside it to be read first. */}
      {!on && <path d="M4 4l16 16" />}
    </svg>
  );
}
